import { createContext } from 'react';

import type { CarPresetData } from '@tmh-website/shared/types/car-preset-data';
import type { Theme } from '@tmh-website/services/electricity-tariff/utils/types';
import { ETFormVariant } from '@tmh-website/services/electricity-tariff/electricity-tariff.service';

import { Locale } from '../const';

const emptyImage = {
  id: 0,
  url: '',
  alternativeText: '',
  type: 'image',
  width: 0,
  height: 0,
  ext: '',
  hash: '',
};

const EMPTY_CAR_PRESET_DATA: CarPresetData = [
  {
    id: 0,
    name: '',
    consumption: 0,
  },
];

export const emptyTheme: Theme = {
  id: 0,
  name: '',
  variant: ETFormVariant.TMH,
  channelId: '',
  kickbackFactor: 1,
  kickbackFactorWithSmartMeter: 1.1,
  fixedKiloWattHourRateInclTax: 0,
  priceListDate: '',
  colorBlue_100: '',
  colorBlue_200: '',
  colorBlue_300: '',
  colorBlue_400: '',
  colorBlue_500_Ultramarine: '',
  colorBlue_600: '',
  colorBlue_700: '',
  colorBlue_800: '',
  colorBlue_900: '',
  colorBlue_1000_Deep: '',
  host: '',
  locale: Locale.En,
  logo: emptyImage,
  emailLogo: emptyImage,
  metaImage: emptyImage,
  metaTitle: '',
  metaDescription: '',
  robots: '',
  footerTopLogo: emptyImage,
  footerLinkGroups: [],
  footerLegalLinks: [],
  footerPaymentPartners: {
    id: 0,
    text: '',
    partnerLogosEntries: [],
  },
  footerCertificates: {
    id: 0,
    text: '',
    partnerLogosEntries: [],
  },
  footerPartners: {
    id: 0,
    text: '',
    partnerLogosEntries: [],
  },
  footerLogisticPartners: {
    id: 0,
    text: '',
    partnerLogosEntries: [],
  },
  footerContactSectionText: '',
  footerContactSectionLink: {},
  navigation: [],
  socialLinks: [],
  tariffName: '',
  tariffNameWithSmartMeter: '',
  powerConsumptionHeadline: '',
  powerConsumptionAddressDescription: '',
  powerConsumptionAddressHeadline: '',
  powerConsumptionAddressInfoText: '',
  powerConsumptionBottomText: '',
  powerConsumptionCarConsumptionCalculatorHeadline: '',
  powerConsumptionCarConsumptionDescription: '',
  powerConsumptionCarConsumptionExactValueHeadline: '',
  powerConsumptionCarConsumptionHeadline: '',
  powerConsumptionCarConsumptionInfoText: '',
  powerConsumptionEmailDoiFlowActive: false,
  powerConsumptionEmailDoiFlowHeadline: '',
  powerConsumptionEmailDoiFlowInitialDescription: '',
  powerConsumptionEmailDoiFlowDoiPendingDescription: '',
  reviewAdvertisingFormParameters: {},
  powerConsumptionTotalConsumptionDescription: '',
  powerConsumptionTotalConsumptionHeadline: '',
  powerConsumptionMeterNumberInfoText: '',
  reviewHeadline: '',
  tariffInfoBasePricePerMonthFootnote: '',
  tariffInfoEVKickbackInfoText: '',
  tariffInfoFlexCoinInfoText: '',
  tariffInfoIntroInfoHeadline: '',
  tariffInfoIntroInfoText: '',
  tariffInfoIntroInfoButton: {},
  tariffInfoPriceGuaranteeFootnote: '',
  tariffFlexCoinValueFootnote: '',
  tariffInfoTerminationFootnote: '',
  tariffInfoTotalPricePerMonthFootnote: '',
  tariffInfoPopupPriceOverviewBottomHeadline: '',
  tariffInfoPopupPriceOverviewBottomText: '',
  tariffInfoPopupPriceOverviewBottomButtonLinkField: {},
  tariffInfoPopupPriceOverviewBottomE14Headline: '',
  tariffInfoPopupPriceOverviewBottomE14Text: '',
  tariffInfoPopupPriceOverviewBottomE14ButtonLinkField: {},
  tariffInfoPopupPriceTableMinimumContractPeriodInfo: '',
  tariffInfoPopupPriceTablePriceGuaranteeInfo: '',
  tariffInfoPopupPriceTablePriceExtendInfo: '',
  tariffInfoPopupPriceTablePriceCancellationPeriodInfo: '',
  tariffInfoPopupPriceTablePricePartialPaymentInfo: '',
  tariffInfoPopupPriceTablePriceConsumptionDataInfo: '',
  tariffInfoPopupMoreInformationHeaderHeadline: '',
  tariffInfoPopupMoreInformationHeaderText: '',
  tariffInfoPopupMoreInformationHeaderLinkField: {},
  tariffInfoPopupMoreInformationHeaderImage: emptyImage,
  tariffInfoPopupMoreInformationContentHeader: '',
  tariffInfoPopupMoreInformationContentText: '',
  tariffInfoE14LawFootnote: '',
  tariffInfoSmartMeterPopupHeadline: '',
  tariffInfoSmartMeterPopupText: '',
  tariffInfoSmartMeterPopupLink: {},
  tariffInfoSmartMeterPopupCheckboxText: '',
  tariffInfoRegularPopupCheckboxText: '',
  tariffInfoSmartMeterSubHeaderSmart: '',
  tariffInfoSmartMeterSubHeaderRegular: '',
  userDataHeadline: '',
  userDataSubHeaderText: '',
  userDataReasonDescription: '',
  userDataReasonLabelMove: '',
  userDataReasonLabelSwitch: '',
  userDataTerminationDescription: '',
  userDataTerminationLabelSelf: '',
  userDataTerminationLabelTMH: '',
  // @TODO florian to check if we could use userDataPaymentMethodDescription
  userDataPaymentDataText: '',
  userDataPaymentMethodDescription: '',
  userDataE14LawHeader: '',
  userDataE14LawSubHeader: '',
  userDataE14LawCheckboxEntries: [],
  userDataE14LawBottomText: '',
  userDataRequiredFieldFootnote: '',
  voucherCodeEnabled: true,
  privacyPolicyBottomText: '',
  reviewCheckboxEntries: [],
  reviewAdvertisingActive: true,
  carPresetData: EMPTY_CAR_PRESET_DATA,
  isFullScreenArrowVisible: false,
  isFullScreenImageHeroTextVisible: false,
  thanksStepButtonRedirectTo: {},
};

export const ElectricityTariffContext = createContext<{
  theme: Theme;
}>({
  theme: emptyTheme,
});
